/**
 * renderCssAnimation - Renders an element's CSS animation at a specific frame
 *
 * This module provides a shared utility function for rendering CSS animations
 * that is exposed on `window` for Puppeteer to call during server-side rendering.
 *
 * Usage by Puppeteer:
 * ```javascript
 * // Call for each frame you want to render, passing the timeline position
 * window.renderCssAnimation(elementId, 0);    // screenshot at time 0 (START stage)
 * window.renderCssAnimation(elementId, 1500); // screenshot at time 1500 (MIDDLE stage)
 * window.renderCssAnimation(elementId, 2500); // screenshot at time 2500 (END stage)
 * ```
 *
 * Prerequisites:
 * - `window.renderDesignData` must be set (done by RenderContainer)
 * - `window.renderZoom` must be set (done by RenderContainer)
 */

import { getAnimationStyles, shouldAnimateElement } from "./animationUtils";

// Type definitions for element data and Redux state
interface CssAnimation {
  name: string;
  direction: string;
  transition: string;
  delay?: number;
  duration?: number;
  textType?: "textbox" | "letter" | "word" | "random";
  stage?: string;
  isPreview?: boolean;
  randomOrder?: number[];
}

interface ElementData {
  uniqueId: string;
  type: string;
  displayValue?: string;
  cssAnimation?: CssAnimation | CssAnimation[];
  width?: number;
  height?: number;
  scaleX?: number;
  scaleY?: number;
}

interface PageData {
  elementsOrder: string[];
}

/**
 * Animation styles returned by getAnimationStyles.
 * Includes CSS animation properties and optionally processedDisplayValue for text animations.
 */
interface AnimationStyles {
  /** For text animations, contains HTML with characters/words wrapped in animated spans */
  processedDisplayValue?: string;
  animationName?: string;
  animationDuration?: string;
  animationDelay?: string;
  animationTimingFunction?: string;
  animationFillMode?: string;
  animationPlayState?: string;
  opacity?: number;
  visibility?: string;
  transform?: string;
  [key: string]: string | number | undefined;
}

export interface DesignData {
  elements: Record<string, ElementData>;
  pages: Record<string, PageData>;
  /** Pre-computed delays for captions, keyed by videoElementId. Used for cross-layer rendering. */
  captionDelays?: Record<string, number>;
}

/**
 * Finds the page ID that contains the specified element.
 * Elements don't store their pageId directly, so we must iterate through
 * all pages and check their elementsOrder arrays, including nested groups.
 *
 * @param elementId - The unique ID of the element
 * @param designData - The design data containing pages and elements
 * @returns The page ID containing the element, or undefined if not found
 */
export const getElementPageId = (
  elementId: string,
  designData: DesignData
): string | undefined => {
  let elementPageId: string | undefined;

  for (const pageId in designData.pages) {
    const page = designData.pages[pageId];
    if (!page) continue;

    // Check if element is directly in this page's elementsOrder
    if (page.elementsOrder.includes(elementId)) {
      elementPageId = pageId;
      break;
    }

    // Check if element is inside a group on this page
    const pageElements = page.elementsOrder.map(
      pageElementId => designData.elements[pageElementId]
    );
    const pageGroups = pageElements.filter(
      pageElement => pageElement && pageElement.type === "group"
    );

    for (const pageGroup of pageGroups) {
      // Groups have their own elementsOrder
      const groupElementsOrder = (pageGroup as { elementsOrder?: string[] })
        .elementsOrder;
      if (groupElementsOrder && groupElementsOrder.includes(elementId)) {
        elementPageId = pageId;
        break;
      }
    }

    if (elementPageId) break;
  }

  return elementPageId;
};

/**
 * Tracks which CSS properties were applied to each element.
 * Used to reset only those properties on subsequent calls, avoiding stale styles.
 *
 * WeakMap ensures automatic cleanup when elements are garbage collected,
 * preventing memory leaks in long-running sessions.
 */
const appliedAnimationProperties = new WeakMap<HTMLElement, string[]>();

/**
 * Applies animation styles to a DOM element.
 * This applies CSS animation properties to make the animation controllable
 * via the Web Animations API.
 *
 * Before applying new styles, resets any properties that were set by previous
 * calls. This mimics React's style reconciliation where each render replaces
 * the entire style attribute with a fresh object.
 *
 * @param element - The DOM element to apply styles to
 * @param animationStyles - The animation styles object from getAnimationStyles
 */
const applyAnimationStylesToElement = (
  element: HTMLElement,
  animationStyles: Record<string, string | number>
): void => {
  // Reset previously applied properties to clear stale styles from prior calls.
  // Only resets properties we previously set, preserving other styles like position.
  const previousProperties = appliedAnimationProperties.get(element) || [];
  for (const property of previousProperties) {
    element.style.removeProperty(property);
  }

  // Track and apply new properties
  const newProperties: string[] = [];
  for (const [key, value] of Object.entries(animationStyles)) {
    // Convert camelCase to kebab-case for CSS properties
    const cssProperty = key.replace(/([A-Z])/g, "-$1").toLowerCase();
    element.style.setProperty(cssProperty, String(value));
    newProperties.push(cssProperty);
  }

  // Store applied properties for reset on next call
  appliedAnimationProperties.set(element, newProperties);
};

/**
 * Creates or finds an animation wrapper div inside the element.
 * This replicates the DOM structure used by React components in editor play mode.
 *
 * The wrapper is needed because:
 * 1. The outer DraggableElement has transformOrigin: "0 0" for positioning
 * 2. Rotation animations expect transformOrigin: center
 * 3. By applying animations to an inner wrapper, we avoid conflicts
 *
 * @param element - The parent element (DraggableElement wrapper)
 * @returns The animation wrapper div (created or existing)
 */
const getOrCreateAnimationWrapper = (element: HTMLElement): HTMLElement => {
  // Check if wrapper already exists (e.g., from previous call)
  const existingWrapper = element.querySelector(
    '[data-animation-wrapper="true"]'
  ) as HTMLElement;
  if (existingWrapper) {
    return existingWrapper;
  }

  // Create new wrapper div matching editor's animation wrapper structure
  const wrapper = document.createElement("div");
  wrapper.setAttribute("data-animation-wrapper", "true");
  wrapper.style.position = "relative";

  // Move all children into the wrapper
  while (element.firstChild) {
    wrapper.appendChild(element.firstChild);
  }

  // Add wrapper to element
  element.appendChild(wrapper);

  return wrapper;
};

/**
 * Renders CSS animations for multiple elements at a specific frame.
 * Batch version of renderCssAnimation for processing multiple elements efficiently.
 *
 * @param elementIds - Array of element IDs to render animations for
 * @param currentTime - The current playback time in milliseconds
 * @returns Array of DOM elements with CSS animations applied (null entries for not found)
 */
export const renderCssAnimations = (
  elementIds: string[],
  currentTime: number = 0
): (HTMLElement | null)[] => {
  return elementIds.map(elementId =>
    renderCssAnimation(elementId, currentTime)
  );
};

/**
 * Renders a CSS animation for an element at a specific frame and returns
 * the DOM element with the CSS animation applied.
 *
 * The function:
 * 1. Gets design data from window.renderDesignData
 * 2. Retrieves element data from designData.elements[elementId]
 * 3. Determines pageId using getElementPageId() logic
 * 4. Gets animation styles using existing utilities
 * 5. Applies animation styles to element/wrapper
 * 6. Returns the animated DOM element
 *
 * @param elementId - The unique ID of the element to render animation for
 * @param currentTime - The current playback time in milliseconds
 * @returns The DOM element with CSS animation applied, or null if not found
 */
export const renderCssAnimation = (
  elementId: string,
  currentTime: number = 0
): HTMLElement | null => {
  // Get design data and zoom from window globals (set by RenderContainer)
  const designData = window.renderDesignData;
  const zoom = window.renderZoom ?? 1;
  // Always playing and paused for Puppeteer frame capture
  const isPlaying = true;
  const isPaused = true;

  if (!designData) {
    console.warn(
      "renderCssAnimation: Design data not available. Set window.renderDesignData first."
    );
    return null;
  }

  // Get element data
  const elementData = designData.elements[elementId];
  if (!elementData) {
    console.warn(
      `renderCssAnimation: Element ${elementId} not found in design data`
    );
    return null;
  }

  // Check if element has animation
  if (!elementData.cssAnimation) {
    // Element has no animation - return element as-is
    const element = document.getElementById(elementId);
    return element;
  }

  // Determine the page ID for this element
  const pageId = getElementPageId(elementId, designData);
  if (!pageId) {
    console.warn(
      `renderCssAnimation: Could not find page for element ${elementId}`
    );
    return null;
  }

  // Check if element should animate
  if (!shouldAnimateElement(elementData, isPlaying, isPaused)) {
    const element = document.getElementById(elementId);
    return element;
  }

  // Get animation styles using existing utility functions
  // This uses the same code path as the editor when timeline is paused,
  // which correctly positions all animation types at the exact frame:
  // - Slide/rise: direct transforms calculated to exact position
  // - Fade/rotate/etc: negative animation-delay to seek to frame
  // - Text (char/word/random): each span gets correct timing
  const animationStyles = getAnimationStyles(
    elementData,
    isPlaying,
    pageId,
    zoom,
    currentTime,
    true
  ) as AnimationStyles | null;

  if (!animationStyles) {
    const element = document.getElementById(elementId);
    return element;
  }

  // Find the outer element (DraggableElement wrapper)
  const element = document.getElementById(elementId);
  if (!element) {
    console.warn(`renderCssAnimation: DOM element ${elementId} not found`);
    return null;
  }

  // Handle text animation DOM changes (processedDisplayValue)
  // For text animations (letter/word/random), getAnimationStyles returns processedDisplayValue
  // which contains the HTML with characters/words wrapped in animated spans
  if (
    animationStyles.processedDisplayValue &&
    typeof animationStyles.processedDisplayValue === "string"
  ) {
    // Find the element that contains the text content
    // For textboxes, this is typically the contenteditable div or inner content
    const textContentElement =
      element.querySelector("[contenteditable]") ||
      element.querySelector(".EditorTextbox-core") ||
      element;

    if (textContentElement) {
      textContentElement.innerHTML = animationStyles.processedDisplayValue;
    }
  }

  // Create or find animation wrapper inside the element
  // This replicates the DOM structure used by React components in editor play mode
  const animationWrapper = getOrCreateAnimationWrapper(element);

  // Apply animation styles to the wrapper (not the outer element)
  applyAnimationStylesToElement(
    animationWrapper,
    animationStyles as Record<string, string | number>
  );

  return animationWrapper;
};

// Type declaration for global window object
declare global {
  interface Window {
    /** Renders CSS animation for a single element at a given time. */
    renderCssAnimation: typeof renderCssAnimation;
    /** Renders CSS animations for multiple elements at a given time (preferred). */
    renderCssAnimations: typeof renderCssAnimations;
    /** Design data set by RenderContainer for Puppeteer access. */
    renderDesignData?: DesignData;
    /** Zoom level set by RenderContainer for Puppeteer access. */
    renderZoom?: number;
  }
}

/**
 * Registers the renderCssAnimation and renderCssAnimations functions on the window object
 * for access by Puppeteer.
 */
export const registerRenderCssAnimation = (): void => {
  window.renderCssAnimation = renderCssAnimation;
  window.renderCssAnimations = renderCssAnimations;
};

export default renderCssAnimation;
